import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { apiClient } from "../../../../../globals/utils/api";

/**
 * Floating bar that appears at the bottom of the page once one or more
 * duplicate groups are checked. Clicking "Bulk Merge" opens a confirmation
 * dialog and POSTs to `/duplicate-accounts/bulk-merge`. Each pair runs in
 * its own backend transaction so partial success is possible — the result
 * dialog summarises wins and failures separately.
 */
const BulkMergeBar = ({ mergeType, selections, onClear, onCompleted }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const pairs = (selections || [])
    .filter((s) => s.keeperId && s.loserIds && s.loserIds.length > 0)
    .flatMap((s) => s.loserIds.map((loserId) => ({ keeper_id: s.keeperId, loser_id: loserId })));

  if (pairs.length === 0) return null;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient(
        "/duplicate-accounts/bulk-merge",
        "POST",
        { merge_type: mergeType, pairs }
      );
      setResult(data);
      if (onCompleted) onCompleted(data);
    } catch (err) {
      setError(err?.detail || err?.message || "Bulk merge failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          left: 16,
          right: 16,
          bottom: 16,
          zIndex: 1100,
          padding: 2,
          borderRadius: 2,
          backgroundColor: "#1e293b",
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selections.length} group(s) selected — {pairs.length} pair(s) will merge
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Each loser is merged into its row's keeper using the recommended keeper unless changed.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClear} sx={{ color: "#ffffff" }}>Clear</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setConfirmOpen(true)}
          >
            Bulk Merge ({pairs.length})
          </Button>
        </Box>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !loading && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm bulk merge</DialogTitle>
        <DialogContent>
          {result ? (
            <Box>
              <Alert severity={result.failure_count === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
                {result.success_count} succeeded, {result.failure_count} failed (of {result.total}).
              </Alert>
              {result.failure_count > 0 && (
                <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                  {result.results
                    .filter((r) => !r.success)
                    .map((r, i) => (
                      <Typography key={i} variant="caption" component="div" sx={{ color: "error.main" }}>
                        keeper={r.keeper_id}, loser={r.loser_id}: {r.error}
                      </Typography>
                    ))}
                </Box>
              )}
            </Box>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Typography variant="body2">
                You are about to merge <strong>{pairs.length}</strong> account pair(s). Each merge runs
                in its own database transaction. The action is reversible per-merge from the Merge
                Log tab within 30 days.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {result ? (
            <Button
              variant="contained"
              onClick={() => {
                setConfirmOpen(false);
                setResult(null);
                if (onClear) onClear();
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setConfirmOpen(false)} disabled={loading}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Merging…</> : "Run merges"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkMergeBar;
